/**
 * PATH: apps/qa/src/lib/offline/types.ts
 * PURPOSE: Type definitions for offline store
 */

import { DBSchema } from 'idb'

export interface PendingUpload {
  id: string
  type: 'evidence' | 'photo' | 'signature' | 'report'
  entityType: 'inspection' | 'area' | 'item' | 'ncr'
  entityId: string
  file: Blob
  fileName: string
  mimeType: string
  metadata: Record<string, any>
  /** Session that queued the upload; required before flush (P6.4). */
  ownerUserId?: string
  ownerOrganisationId?: string
  createdAt: Date
  attempts: number
  lastAttempt?: Date
  error?: string
}

export interface CachedInspection {
  id: string
  data: unknown
  syncedAt: Date
  modifiedAt: Date
  isDirty: boolean
}

export interface CachedChecklistItem {
  id: string
  inspectionId: string
  areaId: string
  data: unknown
  status: 'pending' | 'done' | 'fail' | 'na'
  syncedAt: Date
  modifiedAt: Date
  isDirty: boolean
}

export interface SyncConflict {
  id: string
  entityType: string
  entityId: string
  localData: unknown
  serverData: unknown
  conflictedAt: Date
  resolved: boolean
}

export interface SyncResult {
  success: boolean
  message: string
  synced: number
  failed: number
  conflicts: number
}

export interface QAOfflineDB extends DBSchema {
  inspections: {
    key: string
    value: CachedInspection
    indexes: { 'by-synced': Date; 'by-dirty': boolean }
  }
  checklistItems: {
    key: string
    value: CachedChecklistItem
    indexes: { 'by-inspection': string; 'by-area': string; 'by-dirty': boolean }
  }
  pendingUploads: {
    key: string
    value: PendingUpload
    indexes: { 'by-entity': string; 'by-created': Date; 'by-attempts': number }
  }
  conflicts: {
    key: string
    value: SyncConflict
    indexes: { 'by-entity': string; 'by-resolved': boolean }
  }
  metadata: {
    key: string
    value: { key: string; value: unknown; updatedAt: Date }
  }
}

export const DB_NAME = 'quotech-qa-uploads'
export const DB_VERSION = 1
export const MAX_UPLOAD_ATTEMPTS = 5
export const SYNC_INTERVAL_MS = 30000
